import { computed, ref, type Ref } from "vue";

export interface SelectListOption {
  value: string | number | null;
  text: string;
  disabled?: boolean;
  isGroupHeader?: boolean;
}

const SEARCH_ABOVE_OPTIONS = 8;

export const isSelectableOption = (option: SelectListOption | undefined): boolean =>
  option !== undefined && !option.disabled && !option.isGroupHeader;

export const useSelectList = <Option extends SelectListOption>(
  options: () => Option[],
  searchable: () => boolean,
) => {
  const query: Ref<string> = ref("");

  const isSearchEnabled = computed(
    () => searchable() || options().filter(isSelectableOption).length > SEARCH_ABOVE_OPTIONS,
  );

  const visibleOptions = computed<Option[]>(() => {
    const searchTerm = query.value.trim().toLowerCase();

    if (!isSearchEnabled.value || searchTerm === "") {
      return options();
    }

    const matching = options().filter(
      (option) => !option.isGroupHeader && option.text.toLowerCase().includes(searchTerm),
    );

    return options().filter((option, index, all) => {
      if (!option.isGroupHeader) {
        return matching.includes(option);
      }

      const nextHeaderIndex = all.findIndex(
        (candidate, candidateIndex) => candidateIndex > index && candidate.isGroupHeader,
      );
      const groupEnd = nextHeaderIndex === -1 ? all.length : nextHeaderIndex;

      return all.slice(index + 1, groupEnd).some((candidate) => matching.includes(candidate));
    });
  });

  const firstSelectableIndex = (): number =>
    visibleOptions.value.findIndex((option) => isSelectableOption(option));

  const stepSelectableIndex = (from: number, delta: number): number => {
    let index = from + delta;

    while (index >= 0 && index < visibleOptions.value.length) {
      if (isSelectableOption(visibleOptions.value[index])) {
        return index;
      }

      index += delta;
    }

    return from;
  };

  return { query, isSearchEnabled, visibleOptions, firstSelectableIndex, stepSelectableIndex };
};
